import {
  Mail01Icon,
  MessageMultiple02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { cx } from "shared/utils";
import { PreviewFrame } from "./PreviewFrame";
import { EMAIL_MESSAGE, SMS_MESSAGE, type Message } from "./messages";

export const MessageCard = ({
  message,
  className,
  style,
  size = "md"
}: {
  message: Message;
  className: string;
  style: React.CSSProperties;
  /**
   * "md" is the 481–492px card of the full-size frames. "sm" is the 358×177
   * card of the compact frames (Figma 31:23290): tighter padding, 40px avatar,
   * no timestamp or divider, body wrapping until the card clips it.
   */
  size?: "md" | "sm";
}) => (
  <div
    className={cx(
      "flex flex-col gap-4 overflow-hidden rounded-lg border border-alpha-100 bg-white",
      size === "sm" ? "h-[177px] px-5 pb-4 pt-5" : "px-6 pb-4 pt-6",
      className
    )}
    style={style}
  >
    {/* Header row: avatar + sender + meta */}
    <div className={cx("flex items-center", size === "sm" ? "gap-3" : "gap-4")}>
      <span
        className={cx(
          "flex shrink-0 items-center justify-center rounded-full text-[16px] font-bold leading-6 text-white",
          size === "sm" ? "h-10 w-10" : "h-12 w-12"
        )}
        style={{ backgroundColor: message.avatarColor }}
        aria-hidden="true"
      >
        {message.initials}
      </span>
      <div className="flex min-w-0 flex-col gap-[2px]">
        <p className="text-[14px] font-bold leading-5 text-content-1000">
          Received from {message.sender}
        </p>
        <div className="flex h-5 items-center gap-2 opacity-[0.67]">
          <div className="flex shrink-0 items-center gap-2">
            <HugeiconsIcon
              icon={
                message.channel === "SMS" ? MessageMultiple02Icon : Mail01Icon
              }
              size={20}
              className="text-content-400"
            />
            <span className="text-[14px] font-medium leading-5 text-content-400">
              {message.channel}
            </span>
          </div>
          {size === "md" ? (
            <div className="flex shrink-0 items-center gap-2">
              <span
                className="h-1 w-1 shrink-0 rounded-full bg-content-400"
                aria-hidden="true"
              />
              <span className="text-[14px] font-medium leading-5 text-content-400">
                {message.timestamp}
              </span>
            </div>
          ) : null}
        </div>
      </div>
    </div>

    {/* Divider */}
    {size === "md" ? <div className="h-px w-full bg-alpha-40" /> : null}

    {/* Body — gradient text mask; one truncated line, or wrapping until the
        compact card's fixed height clips it */}
    <p
      className={cx(
        "text-[13px] font-medium",
        size === "sm" ? "leading-[21px]" : "truncate leading-[18px]"
      )}
      style={{
        backgroundImage:
          "linear-gradient(-14.17deg, #1c1d22 19.58%, #717588 93.04%)",
        WebkitBackgroundClip: "text",
        backgroundClip: "text",
        color: "transparent",
        opacity: size === "sm" ? 0.58 : 0.84
      }}
    >
      {message.body}
    </p>
  </div>
);

/**
 * The blurred second card the compact frames show behind the main one (Figma
 * 31:23290 / 394:7658): a full-size message card at 315px, blurred and faded.
 */
export const GhostMessageCard = () => (
  <MessageCard
    message={EMAIL_MESSAGE}
    className="absolute blur-[4.6px]"
    style={{ left: "43.71px", top: "29px", width: "315px", opacity: 0.84 }}
  />
);

export const CommunicationPreview = () => (
  <PreviewFrame height={363} noInnerCard>
    {/* First card — top, right-shifted */}
    <MessageCard
      message={SMS_MESSAGE}
      className="absolute"
      style={{
        left: "103.5px",
        top: "58px",
        width: "481px",
        filter:
          "drop-shadow(0 4px 2px rgba(0,0,0,0.25)) drop-shadow(0 4px 11px rgba(0,0,0,0.11))"
      }}
    />
    {/* Second card — bottom, left-shifted, overlaps first */}
    <MessageCard
      message={EMAIL_MESSAGE}
      className="absolute"
      style={{
        left: "39.5px",
        top: "172px",
        width: "492px",
        filter:
          "drop-shadow(0 -4px 11px rgba(0,0,0,0.11)) drop-shadow(0 4px 2px rgba(0,0,0,0.25))"
      }}
    />
  </PreviewFrame>
);
